// Login.jsx — calls onLoginSuccess({ token, user }) after successful login
// Admin users are automatically redirected to /admin after login.
// FIXED: Forgot password flow — proper validation, error handling, resend, UX polish

import React, { useState, useEffect } from "react";
import "./Login.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../../context/Authcontext";

// const API_BASE = process.env.REACT_APP_API_URL || "";

/* ─────────────────────────────────────────────────────────────
   Small helpers
───────────────────────────────────────────────────────────── */
const isValidEmail  = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone  = (v) => /^[+]?[\d\s\-().]{7,15}$/.test(v.trim());
const isEmailOrPhone = (v) => isValidEmail(v) || isValidPhone(v.replace(/\D/g, ""));

/* ─────────────────────────────────────────────────────────────
   Countdown hook — returns seconds left, resets with resetKey
───────────────────────────────────────────────────────────── */
function useCountdown(seconds, active) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    if (!active) { setLeft(seconds); return; }
    setLeft(seconds);
    const iv = setInterval(() => setLeft(p => { if (p <= 1) { clearInterval(iv); return 0; } return p - 1; }), 1000);
    return () => clearInterval(iv);
  }, [active, seconds]); // eslint-disable-line
  return left;
}

const Login = ({
  isOpen,
  onClose,
  onLoginSuccess,
}) => {
  /* ── Login state ─────────────────────────── */
  const [showPassword, setShowPassword] = useState(false);
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [errorType,    setErrorType]    = useState("");

  /* ── Forgot password state ─────────────── */
  const [showForgot,    setShowForgot]    = useState(false);
  const [forgotStep,    setForgotStep]    = useState("input"); // "input" | "sent"
  const [forgotInput,   setForgotInput]   = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg,     setForgotMsg]     = useState("");
  const [forgotError,   setForgotError]   = useState("");
  const [sentTo,        setSentTo]        = useState("");
  const [resendCount,   setResendCount]   = useState(0);
  const [countdownActive, setCountdownActive] = useState(false);
  const secondsLeft = useCountdown(60, countdownActive);

  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.from
    ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
    : "";
  const { login } = useAuth();

  /* Reset countdown when it hits 0 */
  useEffect(() => {
    if (secondsLeft === 0) setCountdownActive(false);
  }, [secondsLeft]);

  /* ── Session persistence ─────────────── */
  const persistSession = (token, user) => {
    try {
      localStorage.setItem("token",    token);
      localStorage.setItem("user",     JSON.stringify(user));
      localStorage.setItem("th_token", token);
      localStorage.setItem("th_user",  JSON.stringify(user));
    } catch {}
  };

  /* ── Post-login routing ─────────────── */
  const handleSuccess = (token, user) => {
    persistSession(token, user);
    login(token, user);
    if (user.role === "admin") {
      if (onClose) onClose();
      navigate("/admin");
      return;
    }
    if (returnTo) {
      if (onClose) onClose();
      navigate(returnTo, { replace: true });
      return;
    }
    if (typeof onLoginSuccess === "function") {
      onLoginSuccess({ token, user });
      return;
    }
    if (onClose) onClose();
    if (user.role === "employer") navigate("/employer/recruiter-dashboard");
    else                          navigate("/employee/profile");
  };

  /* ── Normal login ───────────────────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setErrorType("");
    if (!email.trim() || !password) {
      setError("Please enter your email/username and password.");
      setErrorType("general");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`/api/login`, {
        email:    email.trim(),
        password,
      });
      handleSuccess(data.token, data.user);
    } catch (err) {
      const status  = err.response?.status;
      const message = err.response?.data?.error || "Login failed. Please try again.";
      if      (status === 404) { setErrorType("email");    setError(message); }
      else if (status === 401) { setErrorType("password"); setError(message); }
      else                     { setErrorType("general");  setError(message); }
    } finally {
      setLoading(false);
    }
  };

  /* ── Google login ────────────────────── */
  const handleGoogleSuccess = async (credentialResponse) => {
    setError(""); setErrorType(""); setLoading(true);
    try {
      const { data } = await axios.post(`/api/login/google`, {
        credential: credentialResponse.credential,
      });
      handleSuccess(data.token, data.user);
    } catch {
      setError("Google login failed. Please try again.");
      setErrorType("general");
    } finally {
      setLoading(false);
    }
  };

  /* ── Forgot password submit ─────────── */
  const handleForgotSubmit = async (e) => {
    e?.preventDefault();
    setForgotError("");
    setForgotMsg("");

    /* Client-side validation */
    const val = forgotInput.trim();
    if (!val) {
      setForgotError("Please enter your email address or phone number.");
      return;
    }
    if (!isEmailOrPhone(val)) {
      setForgotError("Please enter a valid email address or phone number.");
      return;
    }

    /* Resend rate-limit: only 3 resends max */
    if (resendCount >= 3) {
      setForgotError("Too many attempts. Please contact support or try again after some time.");
      return;
    }

    setForgotLoading(true);

    try {
      const isEmail = isValidEmail(val);
      const payload = isEmail ? { email: val } : { mobile: val };

      const { data } = await axios.post(`/api/forgot-password`, payload);

      /* Success ✓ */
      setSentTo(val);
      setForgotStep("sent");
      setResendCount(c => c + 1);
      setCountdownActive(true);

      /* Dev mode: show the reset link in the UI */
      if (data.devResetLink) {
        setForgotMsg(`DEV — Reset link: ${data.devResetLink}`);
      } else {
        setForgotMsg(data.message || (isEmail
          ? `Reset link sent to ${val}. Check your inbox (and spam folder).`
          : `Reset OTP sent to ${val}.`
        ));
      }
    } catch (err) {
      const status  = err.response?.status;
      const message = err.response?.data?.error;

      if (status === 404) {
        setForgotError("No account found with this email or phone number. Please check and try again.");
      } else if (status === 429) {
        setForgotError("Too many requests. Please wait a few minutes before trying again.");
        setCountdownActive(true);
      } else if (status === 400) {
        setForgotError(message || "Invalid email or phone number.");
      } else if (!err.response) {
        /* Network error */
        setForgotError("Network error. Please check your connection and try again.");
      } else {
        setForgotError(message || "Something went wrong. Please try again.");
      }
    } finally {
      setForgotLoading(false);
    }
  };

  /* ── Resend handler ──────────────────── */
  const handleResend = () => {
    if (countdownActive || forgotLoading) return;
    setForgotMsg("");
    setForgotError("");
    handleForgotSubmit();
  };

  /* ── Back to login helper ─────────────── */
  const backToForgotInput = () => {
    setForgotStep("input");
    setForgotMsg("");
    setForgotError("");
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotStep("input");
    setForgotInput("");
    setForgotMsg("");
    setForgotError("");
    setSentTo("");
    setResendCount(0);
    setCountdownActive(false);
  };

  /* ── Register redirect ───────────────── */
  const handleRegister = (e) => {
    e.preventDefault();
    if (onClose) onClose();
    if (location.pathname.startsWith("/employer")) navigate("/employer/signup");
    else                                           navigate("/employee/signup");
  };

  /* ════════════════════════════════════════════════════════════
     FORGOT PASSWORD VIEW
  ════════════════════════════════════════════════════════════ */
  if (showForgot) return (
    <>
      <div className={`drawer-overlay ${isOpen ? "show" : ""}`} onClick={onClose} />
      <div className={`login-drawer ${isOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="drawer-content">

          {/* ── STEP 1: Input ── */}
          {forgotStep === "input" && (
            <>
              <button className="forgot-back-btn" onClick={closeForgot}>
                ← Back to Login
              </button>

              <h2>Reset Your Password</h2>
              <p className="forgot-sub">
                Enter your registered email address or phone number and we'll send you a reset link.
              </p>

              {/* Success banner (e.g. dev link) */}
              {forgotMsg && (
                <div className="login-success" style={{ wordBreak: "break-all" }}>
                  ✅ {forgotMsg}
                </div>
              )}

              {/* Error banner */}
              {forgotError && (
                <div className="login-error">
                  ⚠️ {forgotError}
                </div>
              )}

              <form onSubmit={handleForgotSubmit} noValidate>
                <div className="form-group">
                  <label className="login-label">Email Address or Phone Number</label>
                  <input
                    type="text"
                    placeholder="e.g. priya@email.com or +91 98765 43210"
                    value={forgotInput}
                    onChange={e => {
                      setForgotInput(e.target.value);
                      setForgotError("");
                      setForgotMsg("");
                    }}
                    disabled={forgotLoading}
                    autoFocus
                    className={forgotError ? "input-error" : ""}
                  />
                  {/* Inline hint */}
                  {forgotInput && !isEmailOrPhone(forgotInput) && (
                    <span className="field-hint field-hint--warn">
                      Enter a valid email or phone number
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={forgotLoading || !forgotInput.trim()}
                >
                  {forgotLoading
                    ? <span className="btn-loading"><span className="btn-spinner" />Sending...</span>
                    : "Send Reset Link"
                  }
                </button>
              </form>

              <p className="forgot-sub" style={{ marginTop: 18, fontSize: "0.8rem", textAlign: "center" }}>
                Remembered it?{" "}
                <button className="forgot-link-btn" onClick={closeForgot}>
                  Back to Login
                </button>
              </p>
            </>
          )}

          {/* ── STEP 2: Sent confirmation ── */}
          {forgotStep === "sent" && (
            <>
              <div className="forgot-sent-icon">📬</div>
              <h2 style={{ textAlign: "center" }}>Check Your Inbox</h2>

              <p className="forgot-sub" style={{ textAlign: "center" }}>
                We sent a password reset link to:
              </p>
              <p className="forgot-sent-to">{sentTo}</p>

              {/* Dev link / custom message */}
              {forgotMsg && (
                <div className="login-success" style={{ wordBreak: "break-all", marginBottom: 14 }}>
                  ✅ {forgotMsg}
                </div>
              )}

              {forgotError && (
                <div className="login-error" style={{ marginBottom: 14 }}>
                  ⚠️ {forgotError}
                </div>
              )}

              <div className="forgot-tips">
                <p>📌 Didn't receive it? Try these:</p>
                <ul>
                  <li>Check your <strong>spam / junk</strong> folder</li>
                  <li>Make sure you used the correct email</li>
                  <li>Wait up to 2 minutes for delivery</li>
                </ul>
              </div>

              {/* Resend */}
              <button
                className="login-btn login-btn--outline"
                onClick={handleResend}
                disabled={countdownActive || forgotLoading || resendCount >= 3}
                style={{ marginTop: 10 }}
              >
                {forgotLoading
                  ? <span className="btn-loading"><span className="btn-spinner" />Sending...</span>
                  : countdownActive
                    ? `Resend in ${secondsLeft}s`
                    : resendCount >= 3
                      ? "Max resends reached"
                      : "Resend Reset Link"
                }
              </button>

              {resendCount >= 3 && (
                <p className="forgot-sub" style={{ color: "#394420", fontSize: "0.76rem", textAlign: "center", marginTop: 8 }}>
                  Too many attempts. Please contact support.
                </p>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button className="forgot-link-btn" onClick={backToForgotInput} style={{ flex: 1, textAlign: "center" }}>
                  ← Change email/phone
                </button>
                <button className="forgot-link-btn" onClick={closeForgot} style={{ flex: 1, textAlign: "center" }}>
                  Back to Login
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );

  /* ════════════════════════════════════════════════════════════
     MAIN LOGIN VIEW
  ════════════════════════════════════════════════════════════ */
  return (
    <>
      <div className={`drawer-overlay ${isOpen ? "show" : ""}`} onClick={onClose} />
      <div className={`login-drawer ${isOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="drawer-content">
          <h2>Login to Talent Hive</h2>

          <p className="forgot-sub">
            Sign in to apply for jobs, save listings, and track your applications.
          </p>

          {error && (
            <div className={`login-error ${
              errorType === "email"    ? "error-email"    :
              errorType === "password" ? "error-password" : ""
            }`}>
              ⚠️ {error}
              {errorType === "email" && (
                <span>
                  {" "}
                  <a href="/" onClick={handleRegister} className="error-action-link">
                    Sign up free →
                  </a>
                </span>
              )}
            </div>
          )}

          {loading && <div className="login-loading">Please wait...</div>}

          <form onSubmit={handleLogin} noValidate>
            <div className="form-group">
              <label className="login-label">Email ID / Username</label>
              <input
                type="text"
                placeholder="Enter your email or username"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(""); setErrorType(""); }}
                disabled={loading}
                className={errorType === "email" ? "input-error" : ""}
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label className="login-label">Password</label>
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); setErrorType(""); }}
                  disabled={loading}
                  className={errorType === "password" ? "input-error" : ""}
                  autoComplete="current-password"
                />
                <span
                  className="show-btn"
                  onClick={() => setShowPassword(s => !s)}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁"}
                </span>
              </div>
            </div>

            <div className="forgot-password">
              <button
                type="button"
                className="forgot-link-btn"
                onClick={() => { setShowForgot(true); setError(""); }}
              >
                Forgot Password?
              </button>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading
                ? <span className="btn-loading"><span className="btn-spinner" />Logging in...</span>
                : "Login"
              }
            </button>
          </form>

          {/*
          <div className="divider"><span>Or continue with</span></div>
          <div className="google-login-wrapper">
            Google sign-in is disabled for now.
          </div>
          */}

          <p className="register-text">Don't have an account?</p>

          <div className="register-options">
            <button
              className="candidate-btn"
              onClick={() => { if (onClose) onClose(); navigate("/employee/signup"); }}
            >
              🧑‍💼 Register as Candidate
            </button>
            <button
              className="recruiter-btn"
              onClick={() => { if (onClose) onClose(); navigate("/employer/signup"); }}
            >
              🏢 Register as Recruiter
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
